import { ListChecksIcon } from '@phosphor-icons/react'
import { Handle, type NodeProps, Position, useNodeConnections, useNodeId } from '@xyflow/react'
import { useListCopy } from 'app/hooks/useListCopy'
import { cn } from 'app/lib/utils'
import { labelForToken } from 'app/lib/workflows/dataContext'
import { TASK_PATH_HANDLE } from 'app/lib/workflows/graphUtils'
import { humanizeTokens } from 'app/lib/workflows/messageFallbacks'
import type { CreateTaskFlowNode } from 'app/types/workflows'
import { AppendButton } from './AppendButton'
import { NodeShell } from './NodeShell'

const HANDLE = `!h-2.5 !w-2.5 !border-2 !border-(--surface)`

/** Creates a task for the enrolled contact. Two outlets: the main path carries on at once, and the
 *  task path runs alongside it for the task this step made, which is where Edit task goes. The main
 *  handle is unnamed and rendered first, so edges saved before the task path existed still attach
 *  to it. (Validity comes from the shared issues map via NodeShell.) */
export function CreateTaskNode({ data, selected }: NodeProps<CreateTaskFlowNode>) {
  const copy = useListCopy()
  const nodeId = useNodeId()
  const connections = useNodeConnections({ handleType: `source` })
  const mainOpen = !connections.some((c) => !c.sourceHandle)
  const taskOpen = !connections.some((c) => c.sourceHandle === TASK_PATH_HANDLE)
  return (
    <NodeShell
      icon={ListChecksIcon}
      accent="teal"
      kind="Create task"
      label={data.label}
      description={
        data.title.trim() ? humanizeTokens(data.title, (token) => labelForToken(token, copy)) : `Set up the task`
      }
      selected={selected}
      hasSource={false}
    >
      <div className="flex justify-between px-5 pb-2 text-caption-sm font-semibold">
        <span className="text-default">NEXT</span>
        <span className="text-minimal">TASK</span>
      </div>
      <Handle type="source" position={Position.Bottom} className={cn(HANDLE, `!left-1/4 !bg-inverse`)} />
      <Handle
        id={TASK_PATH_HANDLE}
        type="source"
        position={Position.Bottom}
        className={cn(HANDLE, `!left-3/4 !bg-inverse/40`)}
      />
      {nodeId != null && mainOpen && <AppendButton nodeId={nodeId} className="-bottom-8 left-1/4 -translate-x-1/2" />}
      {nodeId != null && taskOpen && (
        <AppendButton nodeId={nodeId} sourceHandle={TASK_PATH_HANDLE} className="-bottom-8 left-3/4 -translate-x-1/2" />
      )}
    </NodeShell>
  )
}
